// #338: the calendar's and the map's filters live in the URL.
//
// Before this, every filter was component state, so nothing on either page
// could be linked to, bookmarked, or reached with the back button — and the
// date range added in #334 made that limitation obvious the moment anyone
// wanted to share "all of 2026".
//
// The shape:
//
//   /                     upcoming (the API's own default: from now on)
//   /2026                 all of 2026
//   /2026?speaker=1       …by one speaker
//   /?from=…&to=…         a hand-picked range that is not a whole year
//   /map, /map/2026       the same, for the map
//
// The year is a PATH segment because it is the one filter a reader would type
// by hand, and because it is the coarse thing everything else narrows within.
// Everything else is a query parameter.

// Internal filter key → query parameter name. The internal names match what
// `paramsForApi` on each page already expects; the query names are what a
// person sees, so `speakerId` shortens to `speaker`.
export const QUERY_KEYS = Object.freeze({
  speakerId: 'speaker',
  tag: 'tag',
  country: 'country',
  region: 'region',
  city: 'city',
})

// A four-digit year, loosely bounded. The bound is not about data — it is so a
// stray path segment lands on "not found" rather than quietly rendering an
// empty calendar that looks like "no events" (#338 AC).
const MIN_YEAR = 2000
const MAX_YEAR = 2100

export function parseYear(raw) {
  if (raw == null || !/^\d{4}$/.test(String(raw))) return null
  const year = Number(raw)
  return year >= MIN_YEAR && year <= MAX_YEAR ? year : null
}

export function yearBounds(year) {
  return { from: `${year}-01-01`, to: `${year}-12-31` }
}

// The inverse: the year a from/to pair spells out, or null when it is a range
// no year segment can express. This is what lets the picker, the URL and the
// From / To fields stay in agreement without a second source of truth.
export function yearOfRange(from, to) {
  const m = /^(\d{4})-01-01$/.exec(from || '')
  if (!m) return null
  return to === `${m[1]}-12-31` ? Number(m[1]) : null
}

// URL → filters. The year segment wins over any from/to in the query string:
// one of them has to, and the path is the more visible of the two.
export function filtersFromUrl(yearParam, searchParams) {
  const filters = {}
  for (const [key, param] of Object.entries(QUERY_KEYS)) {
    const value = searchParams?.get?.(param)
    if (value) filters[key] = value
  }
  const year = parseYear(yearParam)
  if (year) return { ...filters, ...yearBounds(year) }

  const from = searchParams?.get?.('from')
  const to = searchParams?.get?.('to')
  if (from) filters.from = from
  if (to) filters.to = to
  return filters
}

// filters → URL. `basePath` is '' for the calendar and '/map' for the map.
//
// A whole-year range collapses back into a path segment rather than staying as
// from/to, so choosing "All of 2026" and typing /2026 by hand produce the same
// URL — otherwise the same view would have two spellings and the back button
// would step through both.
export function urlForFilters(basePath, filters = {}) {
  const year = yearOfRange(filters.from, filters.to)
  const path = year ? `${basePath}/${year}` : (basePath || '/')

  const query = new URLSearchParams()
  for (const [key, param] of Object.entries(QUERY_KEYS)) {
    if (filters[key]) query.set(param, filters[key])
  }
  if (!year) {
    if (filters.from) query.set('from', filters.from)
    if (filters.to) query.set('to', filters.to)
  }

  const search = query.toString()
  return search ? `${path}?${search}` : path
}
